import React, { useEffect, useState } from "react";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useAxios } from "../hooks/useAxios";
import "../styles/AdminDashboard.css";

interface Submission {
  id: string;
  registrationHash: string;
  name?: string;
  email?: string;
  artType?: string;
  deadline?: any;
  publicPermission?: boolean;
  contact?: {
    email?: string;
    discord?: string;
    x?: string;
  };
  detail?: string;
  referenceLinks?: string[];
  attachments?: Array<{ url: string; previewUrl: string }>;
  status: string;
  queue?: string;
  createdAt: any;
  updatedAt?: any;
  adminNote?: string;
  reapplyNotifiedAt?: any;
  reapplyReceivedAt?: any;
}

interface WaitingUser {
  id: string;
  email: string;
  hashUrl: string;
  createdAt: any;
  notifiedReopen: boolean;
  isVip: boolean;
  status: string;
  lastNotificationAt?: any;
}

export const AdminDashboard: React.FC = () => {
  const { axiosClient } = useAxios();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string>("");
  const navigate = useNavigate();

  // Submissions state
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRejectedIds, setSelectedRejectedIds] = useState<string[]>([]);

  // Waiting list state
  const [waitingUsers, setWaitingUsers] = useState<WaitingUser[]>([]);
  const [waitingLoading, setWaitingLoading] = useState(false);
  // 選択されたウェイティングユーザーID
  const [selectedWaitingUserIds, setSelectedWaitingUserIds] = useState<
    string[]
  >([]);
  const [waitingActionLoading, setWaitingActionLoading] = useState(false);
  const [waitingActionMessage, setWaitingActionMessage] = useState("");
  const [waitingActionError, setWaitingActionError] = useState("");
  // ウェイティングリストの選択切り替え
  const toggleSelectWaitingUser = (id: string, checked: boolean) => {
    setSelectedWaitingUserIds((prev) =>
      checked
        ? Array.from(new Set([...prev, id]))
        : prev.filter((x) => x !== id),
    );
  };

  const toggleSelectAllWaitingUsers = (checked: boolean) => {
    if (!checked) {
      setSelectedWaitingUserIds([]);
      return;
    }
    const ids = waitingUsers.map((u) => u.id);
    setSelectedWaitingUserIds(ids);
  };

  // 選択ユーザーにリクエストフォームURL送信
  const handleSendRequestFormUrl = async () => {
    if (selectedWaitingUserIds.length === 0) return;
    if (
      !window.confirm(
        `選択された ${selectedWaitingUserIds.length} 人にリクエストフォームのURLを送信します。よろしいですか？`,
      )
    )
      return;
    setWaitingActionLoading(true);
    setWaitingActionMessage("");
    setWaitingActionError("");
    try {
      const resp = await axiosClient.post(
        "/api/waiting/notify",
        { userIds: selectedWaitingUserIds },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      if (resp.data?.success) {
        setWaitingActionMessage(
          `送信完了: 成功 ${resp.data.successCount} / 失敗 ${resp.data.failed}`,
        );
        setSelectedWaitingUserIds([]);
        // 再読み込み
        loadWaitingUsers(idToken);
      } else {
        setWaitingActionError("送信に失敗しました");
      }
    } catch (err: any) {
      setWaitingActionError(
        err.response?.data?.message || "送信に失敗しました",
      );
    } finally {
      setWaitingActionLoading(false);
    }
  };

  // 全ユーザー一覧取得
  const [usersList, setUsersList] = useState<WaitingUser[]>([]);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  // Reopen notification
  const [reopenLoading, setReopenLoading] = useState(false);

  // Commission status
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [lastOpenedAt, setLastOpenedAt] = useState<Date | null>(null);
  // Estimated reopen date (for datepicker)
  const [estimatedReopenDate, setEstimatedReopenDate] = useState<string>("");

  // Gmail connection
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);

  // 返信済み非承認ユーザー一覧
  const [respondedRejected, setRespondedRejected] = useState<Submission[]>([]);

  // 再開時期の目安取得
  const fetchEstimatedReopenDate = async (token: string) => {
    try {
      const resp = await axiosClient.get("/api/waiting/estimated-reopen-date", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data && resp.data.estimatedReopenDate) {
        let dateStr = resp.data.estimatedReopenDate;
        if (
          typeof dateStr === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
        ) {
          setEstimatedReopenDate(dateStr);
        } else {
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) {
            setEstimatedReopenDate(d.toISOString().slice(0, 10));
          }
        }
      } else {
        setEstimatedReopenDate("");
      }
    } catch (err) {
      setEstimatedReopenDate("");
      console.error("Failed to fetch estimated reopen date", err);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;

        if (isAdmin) {
          setUserEmail(user.email);
          const token = await user.getIdToken();
          setIdToken(token);
          checkGmailConnection(token);
          loadSubmissions(token);
          loadWaitingUsers(token);
          loadCommissionStatus(token);
          fetchEstimatedReopenDate(token);
          loadRespondedRejected(token);
          loadAllUsers(token); // 追加
          setLoading(false);
        } else {
          navigate("/admin/login");
        }
      } else {
        navigate("/admin/login");
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkGmailConnection = async (token: string) => {
    try {
      const resp = await axiosClient.get("/api/gmail/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.connected) {
        setGmailConnected(true);
      }
    } catch (err) {
      console.error("Gmail check failed", err);
    }
  };

  const handleConnectGmail = async () => {
    setGmailLoading(true);
    try {
      const resp = await axiosClient.get("/api/gmail/auth-url", {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const url = resp.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setGmailConnected(true);
      }
    } catch (err: any) {
      console.error("Failed to get oauth url", err);
      alert("Gmail接続に失敗しました");
    } finally {
      setGmailLoading(false);
    }
  };

  const loadSubmissions = async (token: string, status?: string) => {
    try {
      const url =
        status && status !== "all"
          ? `/api/admin/submissions?status=${status}`
          : "/api/admin/submissions";
      const resp = await axiosClient.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.ok) {
        setSubmissions(resp.data.submissions || []);
      }
    } catch (err) {
      console.error("Failed to load submissions", err);
      setSubmissions([]);
    }
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setSelectedRejectedIds([]);
    loadSubmissions(idToken, status);
  };

  const loadWaitingUsers = async (token: string) => {
    setWaitingLoading(true);
    try {
      const resp = await axiosClient.get("/api/waiting/list?limit=100", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        setWaitingUsers(resp.data.users);
      }
    } catch (err) {
      console.error("Failed to load waiting users", err);
    } finally {
      setWaitingLoading(false);
    }
  };

  const handleToggleVip = async (user: WaitingUser) => {
    try {
      await axiosClient.post(
        `/api/admin/users/${user.id}/vip`,
        { isVip: !user.isVip },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setWaitingUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isVip: !user.isVip } : u)),
      );
      // 追加: ユーザー一覧も再取得
      await loadAllUsers(idToken);
    } catch (err: any) {
      alert(
        `VIP更新に失敗しました: ${err.response?.data?.message || err.message}`,
      );
    }
  };

  const loadCommissionStatus = async (token: string) => {
    try {
      const resp = await axiosClient.get("/api/waiting/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        setCommissionOpen(resp.data.isOpen);
        const ts = resp.data.lastOpenedAt;
        let parsed: Date | null = null;
        if (ts) {
          if (typeof ts === "string") {
            const d = new Date(ts);
            parsed = isNaN(d.getTime()) ? null : d;
          } else if (typeof ts === "number") {
            parsed = new Date(ts * 1000);
          } else if (ts.seconds) {
            parsed = new Date(ts.seconds * 1000);
          } else if (ts._seconds) {
            parsed = new Date(ts._seconds * 1000);
          }
        }
        setLastOpenedAt(parsed);
      }
      // 追加: estimatedReopenDate取得
      if (resp.data.date) {
        // 文字列（yyyy-mm-dd）ならそのまま、Date型なら変換
        let dateStr = resp.data.date;
        if (
          typeof dateStr === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
        ) {
          setEstimatedReopenDate(dateStr);
        } else {
          // 変換できる場合は変換
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) {
            setEstimatedReopenDate(d.toISOString().slice(0, 10));
          }
        }
      } else {
        setEstimatedReopenDate("");
      }
    } catch (err) {
      console.error("Failed to load commission status", err);
    }
  };

  const loadRespondedRejected = async (token: string) => {
    try {
      const url =
        "/api/admin/submissions?status=rejected&reapplyStatus=responded";
      const resp = await axiosClient.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.ok) {
        setRespondedRejected(resp.data.submissions || []);
      }
    } catch (err) {
      setRespondedRejected([]);
    }
  };

  const loadAllUsers = async (token: string) => {
    try {
      const resp = await axiosClient.get("/api/users/all?limit=100", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.ok) {
        setUsersList(resp.data.users || []);
      }
    } catch (err) {
      setUsersList([]);
      console.error("Failed to load all users", err);
    }
  };

  const handleViewSubmission = (submission: Submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
    setActionMessage("");
    setActionError("");
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;

    setActionLoading(true);
    setActionMessage("");
    setActionError("");

    try {
      await axiosClient.post(
        `/api/admin/submissions/${selectedSubmission.id}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      setActionMessage("承認しました。承認メールを送信しました。");
      loadSubmissions(idToken, filterStatus);
      setTimeout(() => {
        setShowModal(false);
        setSelectedSubmission(null);
      }, 2000);
    } catch (err: any) {
      console.error("Approve failed", err);
      setActionError(err.response?.data?.message || "承認処理に失敗しました");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;

    const estimatedDate = prompt(
      "次回案内のおおよその時期を入力してください（例: 2024年3月頃）",
    );

    setActionLoading(true);
    setActionMessage("");
    setActionError("");

    try {
      await axiosClient.post(
        `/api/admin/submissions/${selectedSubmission.id}/reject`,
        { estimatedDate },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      setActionMessage(
        "非承認しました。ファストレーンに追加し、メールを送信しました。",
      );
      loadSubmissions(idToken, filterStatus);
      setTimeout(() => {
        setShowModal(false);
        setSelectedSubmission(null);
      }, 2000);
    } catch (err: any) {
      console.error("Reject failed", err);
      setActionError(err.response?.data?.message || "非承認処理に失敗しました");
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectRejected = (id: string, checked: boolean) => {
    setSelectedRejectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, id]))
        : prev.filter((x) => x !== id),
    );
  };

  const toggleSelectAllRejected = (checked: boolean) => {
    if (!checked) {
      setSelectedRejectedIds([]);
      return;
    }
    const ids = submissions
      .filter((s) => s.status === "rejected")
      .map((s) => s.id);
    setSelectedRejectedIds(ids);
  };

  const handleSendReopenToRejected = async () => {
    if (selectedRejectedIds.length === 0) return;
    if (
      !window.confirm(
        `選択された ${selectedRejectedIds.length} 件に再開通知を送信します。よろしいですか？`,
      )
    )
      return;
    setActionLoading(true);
    setActionMessage("");
    setActionError("");
    try {
      const resp = await axiosClient.post(
        "/api/admin/submissions/reopen-rejected",
        { submissionIds: selectedRejectedIds },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      if (resp.data?.ok) {
        setActionMessage(
          `送信完了: 成功 ${resp.data.success} / 失敗 ${resp.data.failed}`,
        );
      }
      setSelectedRejectedIds([]);
    } catch (err: any) {
      console.error("Reopen notify (rejected) failed", err);
      setActionError(err.response?.data?.message || "通知送信に失敗しました");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedSubmission) return;

    setActionLoading(true);
    setActionMessage("");
    setActionError("");

    try {
      await axiosClient.post(
        `/api/admin/submissions/${selectedSubmission.id}/complete`,
        {},
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      setActionMessage("依頼を完了に更新しました。");
      loadSubmissions(idToken, filterStatus);
      setTimeout(() => {
        setShowModal(false);
        setSelectedSubmission(null);
      }, 1500);
    } catch (err: any) {
      console.error("Complete failed", err);
      setActionError(err.response?.data?.message || "完了更新に失敗しました");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendReopenNotifications = async () => {
    if (
      !window.confirm(
        "未通知のユーザー全員に再開通知を送信します。よろしいですか？",
      )
    ) {
      return;
    }

    setReopenLoading(true);

    try {
      const resp = await axiosClient.post(
        "/api/waiting/notify",
        {},
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (resp.data.success) {
        alert(
          `再開通知を送信しました。\n成功: ${resp.data.successCount}件\n失敗: ${resp.data.failed}件`,
        );
        // 再読み込み
        loadWaitingUsers(idToken);
        loadCommissionStatus(idToken);
      }
    } catch (err: any) {
      console.error("Reopen notification failed", err);
      alert("再開通知の送信に失敗しました");
    } finally {
      setReopenLoading(false);
    }
  };

  const handleCloseCommission = async () => {
    if (!window.confirm("コミッションの受付を終了します。よろしいですか？")) {
      return;
    }

    setReopenLoading(true);

    try {
      const resp = await axiosClient.patch(
        "/api/waiting/status",
        { isOpen: false },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (resp.data.success) {
        alert("コミッションの受付を終了しました");
        loadCommissionStatus(idToken);
      }
    } catch (err: any) {
      console.error("Close commission failed", err);
      alert("受付終了処理に失敗しました");
    } finally {
      setReopenLoading(false);
    }
  };

  const handleOpenCommission = async () => {
    if (
      !window.confirm(
        "通知を送らずにコミッションの受付を再開します。よろしいですか？",
      )
    ) {
      return;
    }

    setReopenLoading(true);

    try {
      const resp = await axiosClient.patch(
        "/api/waiting/status",
        { isOpen: true },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (resp.data.success) {
        alert("コミッションの受付を再開しました（通知なし）");
        loadCommissionStatus(idToken);
      }
    } catch (err: any) {
      console.error("Open commission failed", err);
      alert("受付再開処理に失敗しました");
    } finally {
      setReopenLoading(false);
    }
  };

  // コミッション再開時期の目安保存（他の箇所と同様に handle~ 形式）
  const handleSaveEstimatedReopenDate = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    if (!estimatedReopenDate) {
      alert("日付を選択してください");
      return;
    }
    try {
      const resp = await axiosClient.post(
        "/api/waiting/estimated-reopen-date",
        { estimatedDate: estimatedReopenDate },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      if (resp?.status && resp.status >= 200 && resp.status < 300) {
        alert("再開時期の目安を保存しました");
      } else {
        alert("保存に失敗しました");
      }
    } catch (err) {
      console.error("Save estimated reopen date failed", err);
      alert("保存に失敗しました");
    }
  };

  const handleTestSend = async () => {
    try {
      await axiosClient.post(
        "/api/admin/test-send",
        { to: userEmail, subject: "テストメール", text: "これはテストです。" },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      alert("テストメールを送信しました");
    } catch (err: any) {
      alert(
        "テストメール送信失敗: " + (err.response?.data?.message || err.message),
      );
    }
  };

  const handleLogout = async () => {
    try {
      // 日本語コメント: ログアウト時にGmail接続のrefresh_tokenをサーバ側で削除します
      try {
        await axiosClient.post(
          "/api/gmail/disconnect",
          {},
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
      } catch (e) {
        console.warn("Gmail disconnect failed (ignored)", e);
      }

      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // タブ切り替え用state
  const [adminTab, setAdminTab] = useState<"submissions" | "respondedRejected">(
    "submissions",
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <h1 className="header-title">管理ダッシュボード</h1>
          <div className="header-user">
            <span className="user-email">{userEmail}</span>
            <button onClick={handleLogout} className="btn-logout">
              ログアウト
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        {/* Gmail Connection Section */}
        <section className="dashboard-section gmail-section">
          <h2>📧 Gmail接続</h2>
          {gmailConnected ? (
            <div className="gmail-connected">
              <p>✓ Gmail接続済み</p>
              <button onClick={handleTestSend} className="btn-test">
                テスト送信
              </button>
            </div>
          ) : (
            <button
              onClick={handleConnectGmail}
              disabled={gmailLoading}
              className="btn-connect"
            >
              {gmailLoading ? "接続中..." : "Gmailを接続"}
            </button>
          )}
        </section>

        {/* Reopen Notification Section */}
        <section className="dashboard-section reopen-section">
          <h2>🔔 コミッション再開通知</h2>
          <div className="commission-status">
            <p>
              現在の受付状態:{" "}
              <span
                className={`status-badge ${commissionOpen ? "approved" : "rejected"}`}
              >
                {commissionOpen ? "受付中" : "停止中"}
              </span>
            </p>
            <p>
              最終再開日時:{" "}
              {lastOpenedAt
                ? new Intl.DateTimeFormat("ja-JP", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(lastOpenedAt)
                : "—"}
            </p>
            {/* コミッション再開時期の目安設定フォーム */}
            <form
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onSubmit={handleSaveEstimatedReopenDate}
            >
              <label htmlFor="estimatedReopenDate">再開時期の目安:</label>
              <input
                id="estimatedReopenDate"
                name="estimatedReopenDate"
                type="date"
                value={estimatedReopenDate}
                onChange={(e) => setEstimatedReopenDate(e.target.value)}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
              />
              <button type="submit" className="btn-reopen">
                保存
              </button>
            </form>
          </div>
          <p>未通知のユーザー全員に再開通知を一斉送信します</p>
          <div className="reopen-actions">
            <button
              onClick={handleSendReopenNotifications}
              disabled={reopenLoading || !gmailConnected}
              className="btn-reopen"
            >
              {reopenLoading ? "送信中..." : "再開通知を送信"}
            </button>
            {commissionOpen ? (
              <button
                onClick={handleCloseCommission}
                disabled={reopenLoading || !gmailConnected}
                className="btn-close"
              >
                受付を終了
              </button>
            ) : (
              <button
                onClick={handleOpenCommission}
                disabled={reopenLoading}
                className="btn-open"
              >
                受付を再開（通知なし）
              </button>
            )}
          </div>
          {!gmailConnected && (
            <p className="warning-text">※ Gmail接続が必要です</p>
          )}
        </section>

        {/* タブ内容 */}
        {adminTab === "submissions" && (
          <>
            {/* Waiting List Section */}
            <section className="dashboard-section waiting-section">
              <div className="section-header">
                <h2>👥 ウェイティングリスト ({waitingUsers.length}人)</h2>
              </div>
              {waitingActionMessage && (
                <div className="success-message">{waitingActionMessage}</div>
              )}
              {waitingActionError && (
                <div className="error-message">{waitingActionError}</div>
              )}
              {waitingLoading ? (
                <p className="no-data">読み込み中...</p>
              ) : waitingUsers.length === 0 ? (
                <p className="no-data">登録者がいません</p>
              ) : (
                <>
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <button
                      className="btn-reopen"
                      onClick={handleSendRequestFormUrl}
                      disabled={
                        waitingActionLoading ||
                        !gmailConnected ||
                        selectedWaitingUserIds.length === 0
                      }
                    >
                      {waitingActionLoading
                        ? "送信中..."
                        : "選択にリクエストフォームURL送信"}
                    </button>
                    <span style={{ fontSize: "0.95em", color: "#888" }}>
                      ※本文は再開通知と同じ内容です
                    </span>
                  </div>
                  <div className="submissions-table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                toggleSelectAllWaitingUsers(e.target.checked)
                              }
                              checked={
                                waitingUsers.length > 0 &&
                                waitingUsers.every((u) =>
                                  selectedWaitingUserIds.includes(u.id),
                                )
                              }
                            />
                          </th>
                          <th>メールアドレス</th>
                          <th>登録日</th>
                          <th>通知済み</th>
                          <th>VIP</th>
                          <th>ステータス</th>
                          <th>ハッシュURL</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waitingUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedWaitingUserIds.includes(
                                  user.id,
                                )}
                                onChange={(e) =>
                                  toggleSelectWaitingUser(
                                    user.id,
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>{user.email}</td>
                            <td>
                              {user.createdAt?._seconds
                                ? new Date(
                                    user.createdAt._seconds * 1000,
                                  ).toLocaleString("ja-JP")
                                : "-"}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${user.notifiedReopen ? "approved" : "pending"}`}
                              >
                                {user.notifiedReopen ? "済" : "未"}
                              </span>
                            </td>
                            <td>{user.isVip ? "✓" : "-"}</td>
                            <td>
                              <span className={`status-badge ${user.status}`}>
                                {user.status}
                              </span>
                            </td>
                            <td>
                              <code style={{ fontSize: "0.85em" }}>
                                /r/{user.hashUrl}
                              </code>
                            </td>
                            <td>
                              <button
                                className="btn-view"
                                onClick={() => handleToggleVip(user)}
                                title={user.isVip ? "VIP解除" : "VIPに追加"}
                              >
                                {user.isVip ? "VIP解除" : "VIPに追加"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>

            {/* Submissions Section */}
            <section className="dashboard-section submissions-section">
              <div className="section-header">
                <h2>📋 依頼一覧</h2>
                <div className="filter-buttons">
                  <button
                    className={filterStatus === "all" ? "active" : ""}
                    onClick={() => handleFilterChange("all")}
                  >
                    すべて
                  </button>
                  <button
                    className={filterStatus === "pending" ? "active" : ""}
                    onClick={() => handleFilterChange("pending")}
                  >
                    保留中
                  </button>
                  <button
                    className={filterStatus === "approved" ? "active" : ""}
                    onClick={() => handleFilterChange("approved")}
                  >
                    承認済み
                  </button>
                  <button
                    className={filterStatus === "rejected" ? "active" : ""}
                    onClick={() => handleFilterChange("rejected")}
                  >
                    非承認
                  </button>
                  {filterStatus === "rejected" && (
                    <button
                      className="btn-reopen"
                      onClick={handleSendReopenToRejected}
                      disabled={
                        actionLoading ||
                        !gmailConnected ||
                        selectedRejectedIds.length === 0
                      }
                      style={{ marginLeft: 8 }}
                    >
                      選択に再開通知を送信
                    </button>
                  )}
                  <button
                    className={filterStatus === "completed" ? "active" : ""}
                    onClick={() => handleFilterChange("completed")}
                  >
                    完了済み
                  </button>
                  <button
                    className={
                      filterStatus === "auto_cancelled" ? "active" : ""
                    }
                    onClick={() => handleFilterChange("auto_cancelled")}
                  >
                    自動キャンセル
                  </button>
                </div>
              </div>

              {submissions.length === 0 ? (
                <p className="no-data">依頼がありません</p>
              ) : (
                <div className="submissions-table">
                  <table>
                    <thead>
                      <tr>
                        {filterStatus === "rejected" && (
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                toggleSelectAllRejected(e.target.checked)
                              }
                              checked={
                                submissions.filter(
                                  (s) => s.status === "rejected",
                                ).length > 0 &&
                                submissions
                                  .filter((s) => s.status === "rejected")
                                  .every((s) =>
                                    selectedRejectedIds.includes(s.id),
                                  )
                              }
                            />
                          </th>
                        )}
                        <th>名前</th>
                        <th>メール</th>
                        <th>タイプ</th>
                        <th>締め切り</th>
                        <th>ステータス</th>
                        <th>キュー</th>
                        <th>作成日</th>
                        <th>更新日</th>
                        <th>再開通知送信</th>
                        <th>返信受信</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((sub) => (
                        <tr
                          key={sub.id}
                          className={
                            sub.queue === "vip"
                              ? "vip-row"
                              : sub.queue === "fastlane"
                                ? "fastlane-row"
                                : undefined
                          }
                        >
                          {filterStatus === "rejected" && (
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedRejectedIds.includes(sub.id)}
                                onChange={(e) =>
                                  toggleSelectRejected(sub.id, e.target.checked)
                                }
                              />
                            </td>
                          )}
                          <td>
                            {sub.name || "未入力"}
                            {sub.queue === "vip" && (
                              <span
                                className="vip-badge"
                                style={{ marginLeft: 8 }}
                              >
                                VIP
                              </span>
                            )}
                            {sub.queue === "fastlane" && (
                              <span
                                className="fastlane-badge"
                                style={{ marginLeft: 8 }}
                              >
                                FastLane
                              </span>
                            )}
                          </td>
                          <td>{sub.email}</td>
                          <td>{sub.artType || "-"}</td>
                          <td>
                            {sub.deadline?._seconds
                              ? new Date(
                                  sub.deadline._seconds * 1000,
                                ).toLocaleDateString("ja-JP")
                              : "-"}
                          </td>
                          <td>
                            <span className={`status-badge ${sub.status}`}>
                              {sub.status}
                            </span>
                          </td>
                          <td>
                            {sub.queue === "vip" ? (
                              <span className="vip-badge">VIP</span>
                            ) : sub.queue === "fastlane" ? (
                              <span className="fastlane-badge">FastLane</span>
                            ) : (
                              "normal"
                            )}
                          </td>
                          <td>
                            {sub.createdAt?._seconds
                              ? new Date(
                                  sub.createdAt._seconds * 1000,
                                ).toLocaleString("ja-JP")
                              : "-"}
                          </td>
                          <td>
                            {sub.updatedAt?._seconds
                              ? new Date(
                                  sub.updatedAt._seconds * 1000,
                                ).toLocaleString("ja-JP")
                              : "-"}
                          </td>
                          <td>
                            {sub.reapplyNotifiedAt?._seconds
                              ? new Date(
                                  sub.reapplyNotifiedAt._seconds * 1000,
                                ).toLocaleString("ja-JP")
                              : "-"}
                          </td>
                          <td>
                            {sub.reapplyReceivedAt?._seconds
                              ? new Date(
                                  sub.reapplyReceivedAt._seconds * 1000,
                                ).toLocaleString("ja-JP")
                              : "-"}
                          </td>
                          <td>
                            <button
                              onClick={() => handleViewSubmission(sub)}
                              className="btn-view"
                            >
                              詳細
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Users List Section */}
            <section className="dashboard-section users-section">
              <h2>👤 ユーザー一覧</h2>
              <div className="submissions-table">
                <table>
                  <thead>
                    <tr>
                      <th>メールアドレス</th>
                      <th>VIP</th>
                      <th>ユーザーハッシュ</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="no-data">
                          登録ユーザーがいません
                        </td>
                      </tr>
                    ) : (
                      usersList.map((user) => (
                        <tr key={user.id}>
                          <td>{user.email}</td>
                          <td>{user.isVip ? "✓" : "-"}</td>
                          <td>
                            <code style={{ fontSize: "0.85em" }}>
                              {user.hashUrl ? `/r/${user.hashUrl}` : "-"}
                            </code>
                          </td>
                          <td>
                            <button
                              className="btn-view"
                              onClick={() => handleToggleVip(user)}
                              title={user.isVip ? "VIP解除" : "VIPに追加"}
                            >
                              {user.isVip ? "VIP解除" : "VIPに追加"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        {adminTab === "respondedRejected" && (
          <section className="dashboard-section responded-rejected-section">
            <h2>返信済み非承認ユーザー</h2>
            {respondedRejected.length === 0 ? (
              <p className="no-data">返信済みの非承認ユーザーはありません</p>
            ) : (
              <div className="submissions-table">
                <table>
                  <thead>
                    <tr>
                      <th>名前</th>
                      <th>メール</th>
                      <th>タイプ</th>
                      <th>ステータス</th>
                      <th>reapplyStatus</th>
                      <th>作成日</th>
                      <th>更新日</th>
                    </tr>
                  </thead>
                  <tbody>
                    {respondedRejected.map((sub) => (
                      <tr key={sub.id}>
                        <td>{sub.name || "未入力"}</td>
                        <td>{sub.email}</td>
                        <td>{sub.artType || "-"}</td>
                        <td>
                          <span className={`status-badge ${sub.status}`}>
                            {sub.status}
                          </span>
                        </td>
                        <td>{"responded"}</td>
                        <td>
                          {sub.createdAt?._seconds
                            ? new Date(
                                sub.createdAt._seconds * 1000,
                              ).toLocaleString("ja-JP")
                            : "-"}
                        </td>
                        <td>
                          {sub.updatedAt?._seconds
                            ? new Date(
                                sub.updatedAt._seconds * 1000,
                              ).toLocaleString("ja-JP")
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Modal */}
      {showModal && selectedSubmission && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>依頼詳細</h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>名前:</label>
                <span>{selectedSubmission.name || "未入力"}</span>
              </div>
              <div className="detail-row">
                <label>メール:</label>
                <span>{selectedSubmission.email}</span>
              </div>
              <div className="detail-row">
                <label>イラストタイプ:</label>
                <span>{selectedSubmission.artType || "-"}</span>
              </div>
              <div className="detail-row">
                <label>希望締め切り:</label>
                <span>
                  {selectedSubmission.deadline?._seconds
                    ? new Date(
                        selectedSubmission.deadline._seconds * 1000,
                      ).toLocaleString("ja-JP")
                    : "指定なし"}
                </span>
              </div>
              <div className="detail-row">
                <label>公開許可:</label>
                <span>
                  {selectedSubmission.publicPermission ? "可" : "不可"}
                </span>
              </div>
              <div className="detail-row">
                <label>連絡先:</label>
                <span>
                  {selectedSubmission.contact?.email && (
                    <>Email: {selectedSubmission.contact.email}</>
                  )}
                  {selectedSubmission.contact?.discord && (
                    <>Discord: {selectedSubmission.contact.discord}</>
                  )}
                  {selectedSubmission.contact?.x && (
                    <>X: {selectedSubmission.contact.x}</>
                  )}
                </span>
              </div>
              <div className="detail-row">
                <label>依頼詳細:</label>
                <div className="detail-text">
                  {selectedSubmission.detail || "なし"}
                </div>
              </div>
              {/* 参考リンク表示 */}
              {selectedSubmission.referenceLinks &&
                selectedSubmission.referenceLinks.length > 0 && (
                  <div className="detail-row">
                    <label>参考リンク:</label>
                    <ul className="links-list">
                      {selectedSubmission.referenceLinks.map((link, i) => (
                        <li key={i}>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ wordBreak: "break-all" }}
                          >
                            {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {/* 添付画像サムネイル表示 */}
              {selectedSubmission.attachments &&
                selectedSubmission.attachments.length > 0 && (
                  <div className="detail-row">
                    <label>添付画像:</label>
                    <div
                      className="attachments-preview-list"
                      style={{ display: "flex", flexWrap: "wrap", gap: 12 }}
                    >
                      {selectedSubmission.attachments.map((att, i) => (
                        <div
                          key={i}
                          className="attachment-preview-item"
                          style={{ textAlign: "center" }}
                        >
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={att.previewUrl || att.url}
                              style={{
                                maxWidth: 100,
                                maxHeight: 100,
                                borderRadius: 6,
                                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                marginBottom: 4,
                              }}
                              alt="attachment thumbnail"
                            />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              <div className="detail-row">
                <label>ステータス:</label>
                <span className={`status-badge ${selectedSubmission.status}`}>
                  {selectedSubmission.status}
                </span>
              </div>
              <div className="detail-row">
                <label>キュー:</label>
                <span>
                  {selectedSubmission.queue || "normal"}
                  {selectedSubmission.queue === "vip" && (
                    <span className="vip-badge" style={{ marginLeft: 8 }}>
                      VIP
                    </span>
                  )}
                  {selectedSubmission.queue === "fastlane" && (
                    <span className="fastlane-badge" style={{ marginLeft: 8 }}>
                      FastLane
                    </span>
                  )}
                </span>
              </div>

              {actionMessage && (
                <div className="success-message">{actionMessage}</div>
              )}
              {actionError && (
                <div className="error-message">{actionError}</div>
              )}
            </div>

            {selectedSubmission.status === "pending" && (
              <div className="modal-footer">
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="btn-approve"
                >
                  {actionLoading ? "処理中..." : "承認"}
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="btn-reject"
                >
                  {actionLoading ? "処理中..." : "非承認"}
                </button>
              </div>
            )}
            {(selectedSubmission.status === "approved" ||
              selectedSubmission.status === "in_progress") && (
              <div className="modal-footer">
                <button
                  onClick={handleComplete}
                  disabled={actionLoading}
                  className="btn-complete"
                >
                  {actionLoading ? "処理中..." : "完了に更新"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
